import { createAction } from 'redux-act';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import firebase from 'firebase.js';
import { firebaseError } from 'utils';
import {
  fetchCollection,
  fetchDocument,
  createDocument,
  updateDocument,
  deleteDocument,
} from '../api';

// Fetch
export const COUNTRIES_FETCH_DATA_INIT = createAction('COUNTRIES_FETCH_DATA_INIT');
export const COUNTRIES_FETCH_DATA_SUCCESS = createAction('COUNTRIES_FETCH_DATA_SUCCESS');
export const COUNTRIES_FETCH_DATA_FAIL = createAction('COUNTRIES_FETCH_DATA_FAIL');

// Delete
export const COUNTRIES_DELETE_COUNTRY_INIT = createAction('COUNTRIES_DELETE_COUNTRY_INIT');
export const COUNTRIES_DELETE_COUNTRY_SUCCESS = createAction('COUNTRIES_DELETE_COUNTRY_SUCCESS');
export const COUNTRIES_DELETE_COUNTRY_FAIL = createAction('COUNTRIES_DELETE_COUNTRY_FAIL');

// Create
export const COUNTRIES_CREATE_COUNTRY_INIT = createAction('COUNTRIES_CREATE_COUNTRY_INIT');
export const COUNTRIES_CREATE_COUNTRY_SUCCESS = createAction('COUNTRIES_CREATE_COUNTRY_SUCCESS');
export const COUNTRIES_CREATE_COUNTRY_FAIL = createAction('COUNTRIES_CREATE_COUNTRY_FAIL');

// Modify
export const COUNTRIES_MODIFY_COUNTRY_INIT = createAction('COUNTRIES_MODIFY_COUNTRY_INIT');
export const COUNTRIES_MODIFY_COUNTRY_SUCCESS = createAction('COUNTRIES_MODIFY_COUNTRY_SUCCESS');
export const COUNTRIES_MODIFY_COUNTRY_FAIL = createAction('COUNTRIES_MODIFY_COUNTRY_FAIL');

// Cleanup
export const COUNTRIES_CLEAN_UP = createAction('COUNTRIES_CLEAN_UP');

export const fetchCountries = (countryId) => async (dispatch, getState) => {
  dispatch(COUNTRIES_FETCH_DATA_INIT());

  if (countryId) {
    let country;
    try {
      country = await fetchDocument('countries', countryId);
      country.createdAt = moment(country.createdAt.toDate()).format('ddd MMM DD YYYY');
    } catch (error) {
      const { locale } = getState().preferences;
      toastr.error('', firebaseError(error.code, locale));
      return dispatch(COUNTRIES_FETCH_DATA_FAIL({ error }));
    }

    if (!country) {
      const errorMessage = 'Country not available';
      toastr.error('', errorMessage);
      return dispatch(COUNTRIES_FETCH_DATA_FAIL({ error: errorMessage }));
    }

    const existing = getState().countries.data;
    const countries = existing.some((c) => c.id === country.id)
      ? existing.map((c) => (c.id === country.id ? country : c))
      : [...existing, country];
    return dispatch(COUNTRIES_FETCH_DATA_SUCCESS({ data: countries }));
  }

  let countries;
  try {
    countries = await fetchCollection('countries');
    countries.map((countryItem) => {
      const copy = countryItem;
      copy.createdAt = moment(copy.createdAt.toDate()).format('ddd MMM DD YYYY');
      return copy;
    });
  } catch (error) {
    const { locale } = getState().preferences;
    toastr.error('', firebaseError(error.code, locale));
    return dispatch(COUNTRIES_FETCH_DATA_FAIL({ error }));
  }
  return dispatch(COUNTRIES_FETCH_DATA_SUCCESS({ data: countries }));
};

export const deleteCountry = (id) => async (dispatch, getState) => {
  dispatch(COUNTRIES_DELETE_COUNTRY_INIT());

  // Referential integrity check: query vendors that reference this country
  let referencingVendors;
  try {
    referencingVendors = await fetchCollection('vendors', {
      queries: [{ attribute: 'countries', operator: 'array-contains', value: id }],
    });
  } catch (error) {
    const { locale } = getState().preferences;
    toastr.error('', firebaseError(error.code, locale));
    return dispatch(COUNTRIES_DELETE_COUNTRY_FAIL({ error }));
  }

  if (referencingVendors && referencingVendors.length > 0) {
    const vendorNames = referencingVendors.map((v) => v.name).join(', ');
    const errorMessage = `Cannot delete country. It is used by vendors: ${vendorNames}. Remove the country from those vendors first.`;
    toastr.error('', errorMessage);
    return dispatch(COUNTRIES_DELETE_COUNTRY_FAIL({ error: errorMessage }));
  }

  try {
    await deleteDocument('countries', id);
  } catch (error) {
    const { locale } = getState().preferences;
    toastr.error('', firebaseError(error.code, locale));
    return dispatch(COUNTRIES_DELETE_COUNTRY_FAIL({ error }));
  }

  toastr.success('', 'The country was successfully deleted');
  return dispatch(COUNTRIES_DELETE_COUNTRY_SUCCESS({ id }));
};

export const createCountry = ({ name, locale }) => async (dispatch, getState) => {
  dispatch(COUNTRIES_CREATE_COUNTRY_INIT());

  const ref = firebase.firestore().collection('countries').doc();
  const uid = ref.id;

  const countryData = {
    name,
    locale,
    createdAt: moment(Date()).toDate(),
  };

  try {
    await createDocument('countries', uid, countryData);
  } catch (error) {
    const { locale: appLocale } = getState().preferences;
    toastr.error('', firebaseError(error.code, appLocale));
    return dispatch(COUNTRIES_CREATE_COUNTRY_FAIL({ error }));
  }

  toastr.success('', 'Country created successfully');
  return dispatch(COUNTRIES_CREATE_COUNTRY_SUCCESS({ country: { ...countryData, id: uid } }));
};

export const modifyCountry = ({ name, locale, id }) => async (dispatch, getState) => {
  dispatch(COUNTRIES_MODIFY_COUNTRY_INIT());

  const countryData = {
    name,
    locale,
    createdAt: moment(Date()).toDate(),
  };

  try {
    await updateDocument('countries', id, countryData);
  } catch (error) {
    const { locale: appLocale } = getState().preferences;
    toastr.error('', firebaseError(error.code, appLocale));
    return dispatch(COUNTRIES_MODIFY_COUNTRY_FAIL({ error }));
  }

  toastr.success('', 'Country updated successfully');
  return dispatch(COUNTRIES_MODIFY_COUNTRY_SUCCESS({ country: { name, locale, createdAt: countryData.createdAt, id } }));
};

export const countriesCleanUp = () => (dispatch) => dispatch(COUNTRIES_CLEAN_UP());
